import React, { useMemo, useState, useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import App from '../../../App';
import MyHomeAspirationsStyles from './MyHomeAspirations.styled';
import * as dealerService from '../../../services/dealerService';
import * as homesService from '../../../services/homesService';
import { formatDealerName } from 'pubweb-smokey/dist/utils/formatters';
import { parseInitialPropsContext } from '../../../utils';
import { Theme as ThemeProvider } from 'dx-sdk/build/providers';
import {
  Button,
  Hero,
  IconCard,
  Picture,
  ItemsGrid,
  Typography,
  NextStepCard,
  PlannerQuestion,
  PlannerQuestionnaire,
  PlannerField,
  PlannerFields,
  FeaturesGrid,
  FeatureCard,
  Stack,
  LinkArrow,
  TextInput,
  RemoteSVG,
} from 'dx-sdk/build/components';
import { Arrow, Pencil, PencilAndPaper } from 'dx-sdk/build/icons';
import bars from '../../../images/portal/icons/bars.svg';
import heart from '../../../images/portal/icons/heart.svg';
import grid from '../../../images/portal/icons/grid.svg';
import { Link, useLocation, useHistory, Redirect } from 'react-router-dom';
import { findFeaturedHomes } from '../../../utils/utils';
import PlannerPreferencesHeroBG from '../../../images/portal/planner/preferences/PlannerPreferencesHeroBG.jpg';
import ReviewNextStepIcon from '../../../images/portal/planner/review/ReviewNextStepIcon.svg';
import { smoothScroll } from '../../../components/Shared/SmokeyTransitional/utils/utils';
import { DXAccountContext } from '../../../contexts/DXAccountContext';
import Loading from '../../../images/svg/icons/Loading';
import { useAuthorization } from 'dx-sdk/build/authorization';
import HomeAspirationsIcon from '../../components/PlannerIcons/HomeAspirationsIcon';
import {
  portalIconProgress,
  findAspirationHomes,
} from '../../utils/PortalUtils';
import RecommendedHomes from '../../components/RecommendedHomes/RecommendedHomes';
import { InView } from 'react-intersection-observer';
import useWindowSize from '../../../hooks/useWindowSize';
import BuiltSmartLogo from '../../../images/BuiltSmartCentered.svg';
import DesignSmartLogo from '../../../images/DesignSmartCentered.svg';
import EnergySmartLogo from '../../../images/EnergySmartCentered.svg';
import BudgetSmartLogo from '../../../images/BudgetSmartCentered.svg';

const MyHomeAspirations = ({ dealer, homes }) => {
  const [width, height] = useWindowSize();
  const dxContext = useContext(DXAccountContext);
  const [activeQuestion, setActiveQuestion] = useState(1);
  const [bedError, setBedError] = useState('');
  const [bathError, setBathError] = useState('');
  const [bedBathButtonDisabled, setBedBathButtonDisabled] = useState(false);
  const [featureCards, setFeatureCards] = useState([]);
  const [orderedFeatures, setOrderedFeatures] = useState(null);
  const { isAuthenticated, isLoading } = useAuthorization();
  const [formIsSaved, setFormIsSaved] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const [isDisabled, setIsDisabled] = useState(false);
  const [updatedUserProfile, setUpdatedUserProfile] = useState(null);
  const [updatedUser, setUpdatedUser] = useState({
    userId: '',
  });

  const [aspirationFormSavedHCs, setAspirationFormSavedHCs] = useState([]);
  const [aspirationFormSavedProfileIds, setAspirationFormSavedProfileIds] =
    useState([]);

  useEffect(() => {
    const items = JSON.parse(localStorage.getItem('aspiration_fs'));
    if (items) {
      setAspirationFormSavedHCs(items.homeCenters);
      setAspirationFormSavedProfileIds(items.profileIds);
    }
  }, []);

  useEffect(() => {
    if (aspirationFormSavedHCs && aspirationFormSavedHCs.length) {
      let filteredHCList = [...new Set(aspirationFormSavedHCs)];
      let filteredProfileIdList = [...new Set(aspirationFormSavedProfileIds)];
      localStorage.setItem(
        'aspiration_fs',
        JSON.stringify({
          homeCenters: filteredHCList,
          profileIds: filteredProfileIdList,
        })
      );
    }
  }, [aspirationFormSavedHCs, aspirationFormSavedProfileIds]);

  useEffect(() => {
    let lotsStored =
      aspirationFormSavedHCs && aspirationFormSavedHCs.length
        ? aspirationFormSavedHCs?.map((i) => i)
        : [];
    let profileIdsStored =
      aspirationFormSavedProfileIds && aspirationFormSavedProfileIds.length
        ? aspirationFormSavedProfileIds?.map((i) => i)
        : [];
    if (
      !location?.state?.activeQuestionLink &&
      lotsStored.includes(dealer?.dealerId) &&
      profileIdsStored.includes(updatedUserProfile?.profileId)
    ) {
      setFormIsSaved(true);
    }
  }, [
    aspirationFormSavedHCs,
    aspirationFormSavedProfileIds,
    updatedUserProfile?.profileId,
  ]);

  useEffect(() => {
    if (location?.state?.activeQuestionLink) {
      history.replace({ state: { dealer, homes } }) +
        setFormIsSaved(false) +
        setActiveQuestion(location?.state?.activeQuestionLink) +
        smoothScroll('#plannerSection', -20);
    }
  }, []);

  const aspirationsPlannerProgressObj =
    dxContext?.state?.account?.plannerProgress?.find(
      (item) => item.section == 'Home Aspirations'
    );

  const portalIcon = portalIconProgress(
    aspirationsPlannerProgressObj?.percentCompleted || 0
  );

  useEffect(() => {
    if (
      dxContext?.state?.featuresServiceLoading !== true &&
      dxContext?.state?.isAuthenticated
    ) {
      setOrderedFeatures(
        dxContext?.state?.features?.reduce((features, feature) => {
          const accountLiked =
            !!dxContext?.state?.account?.profile?.likedFeatures?.find(
              ({ featureId: likedFeatureId }) =>
                feature.featureId === likedFeatureId
            );
          return accountLiked
            ? [
                {
                  ...feature,
                  favorite: true,
                },
                ...features,
              ]
            : [...features, feature];
        }, [])
      );
    }
  }, [
    dxContext?.state?.featuresServiceLoading,
    dxContext?.state?.account?.profile,
    dxContext?.state?.isAuthenticated,
  ]);

  useEffect(() => {
    setFeatureCards(orderedFeatures);
  }, [orderedFeatures]);

  useEffect(() => {
    if (location?.state?.activeQuestionLink) {
      history.replace({ state: { dealer, homes } }) +
        setFormIsSaved(false) +
        setActiveQuestion(location?.state?.activeQuestionLink) +
        smoothScroll('#plannerSection', -20);
    }
  }, []);

  useEffect(() => {
    if (
      dxContext?.state?.account &&
      dxContext?.state?.account?.profile &&
      !updatedUser.userId
    ) {
      setUpdatedUserProfile(dxContext?.state?.account.profile);
      setUpdatedUser({
        userId: dxContext?.state?.account.userId,
      });
    }
  }, [dxContext?.state?.account]);

  const handleFeatureClick = (featureId, featureName) => {
    const isLiked = !!dxContext?.state?.account?.profile?.likedFeatures.find(
      ({ featureId: likedFeatureId }) => featureId === likedFeatureId
    );

    if (!isLiked) {
      if (!isDisabled) {
        dxContext?.actions.addFeature(featureId);
      }
    } else {
      dxContext?.actions.removeFeature(featureId);
    }
  };

  useEffect(() => {
    if (
      dxContext?.state?.account?.profile?.likedFeatures &&
      dxContext?.state?.account?.profile?.likedFeatures.length !== 0
    ) {
      dxContext?.actions?.markContentComplete(
        aspirationsPlannerProgressObj,
        'home_aspiration_question_2'
      );
    } else {
      dxContext?.actions?.markContentIncomplete(
        aspirationsPlannerProgressObj,
        'home_aspiration_question_2'
      );
    }
  }, [dxContext?.state?.account?.profile?.likedFeatures]);

  const BackLink = () => {
    return (
      <LinkArrow
        isReversed
        components={{ Root: Link }}
        componentProps={{
          to: { pathname: '/portal/planner', state: { dealer, homes } },
        }}
      >
        Back to MyHome Planner
      </LinkArrow>
    );
  };

  const PreferencesLink = ({ children }) => {
    return (
      <NextStepCard
        {...children}
        components={{
          Link: () => (
            <Link
              className="NextStepCard-link"
              to={{
                pathname: '/portal/planner',
                state: { dealer, homes },
              }}
            >
              <Arrow />
            </Link>
          ),
        }}
      />
    );
  };

  const CustomAction = (value, questionNumber) => {
    return (
      <div
        className="DXButton DXButton-root"
        onClick={() => {
          setFormIsSaved(false) + setActiveQuestion(questionNumber);
        }}
      >
        {value ? (
          <>
            <Pencil /> <span className="hide-on-mobile">Edit</span>
          </>
        ) : (
          <>
            <PencilAndPaper />
            <span className="hide-on-mobile">COMPLETE YOUR ANSWER</span>
          </>
        )}
      </div>
    );
  };

  const aspirationHomes = findAspirationHomes(
    dxContext?.state?.account?.profile?.preferredBeds,
    dxContext?.state?.account?.profile?.preferredBaths,
    dxContext?.state?.account?.profile?.likedFeatures,
    homes
  );

  const defaultHomes = findFeaturedHomes(homes);

  const homesList = useMemo(() => {
    const listArray =
      aspirationHomes && aspirationHomes.length > 0
        ? aspirationHomes
        : defaultHomes;

    return listArray?.map((item) => {
      return {
        id: item.serialNumber || item.modelNumber,
        retailHomeObject: {
          ...item,
        },
      };
    });
  }, [dxContext?.state?.account?.profile]);

  const handleProfileFieldUpdate = (key) => (value) => {
    const newProfileObj = {
      ...updatedUserProfile,
      [key]: value,
    };
    setUpdatedUserProfile(newProfileObj);
    dxContext?.actions?.updateProfileRetail(newProfileObj);
  };

  useEffect(() => {
    if (
      updatedUserProfile?.preferredBeds &&
      updatedUserProfile?.preferredBaths
    ) {
      setBedError(null);
      setBathError(null);
      dxContext?.actions?.markContentComplete(
        aspirationsPlannerProgressObj,
        'home_aspiration_question_1'
      );
    }
  }, [updatedUserProfile?.preferredBeds, updatedUserProfile?.preferredBaths]);

  const TriggerBtn = () => {
    return (
      <button
        className="DXButton DXButton-root DX-TriggerBtn PlannerQuestionnaire-trigger"
        onClick={() => {
          if (
            updatedUserProfile?.preferredBeds &&
            !updatedUserProfile?.preferredBaths
          ) {
            setBathError('Select Bath Preference');
          } else if (
            updatedUserProfile?.preferredBeds &&
            !updatedUserProfile?.preferredBaths
          ) {
            setBedError('Select Bed Preference');
          } else {
            setBedError(null);
            setBathError(null);
            setFormIsSaved(true);
          }
        }}
      >
        NEXT <div className="background"></div>
      </button>
    );
  };

  const isMobile = width <= 600;

  const featureCardList = orderedFeatures;

  if (!isAuthenticated && !isLoading) {
    return <Redirect to="/myhome-account-login" />;
  }

  return (
    <ThemeProvider theme={{}}>
      <App dealer={dealer} homes={homes}>
        <MyHomeAspirationsStyles>
          <Helmet>
            <title>
              My Home Aspirations | {formatDealerName(dealer?.dealerName)}
            </title>
            <meta name="robots" content="noindex" />
          </Helmet>

          {dxContext?.state?.globalLoading ? (
            <div className="loading-screen-panel">
              <Loading width="150px" />
            </div>
          ) : (
            <>
              <Hero
                components={{
                  Backdrop: Picture,
                }}
                componentsProps={{
                  Backdrop: {
                    src: PlannerPreferencesHeroBG,
                  },
                  Container: {
                    style: {
                      margin: '0px auto',
                    },
                  },
                }}
              >
                <IconCard
                  heading="My Home Aspirations"
                  icon={
                    <HomeAspirationsIcon
                      dashArray={portalIcon?.dashArray}
                      dashOffset={portalIcon?.dashOffset}
                      opacity={portalIcon?.opacity}
                      showStar={portalIcon?.showStar}
                    />
                  }
                  subheading="Back to MyHome Planner"
                  iconOverlay
                  components={{
                    Subheading: BackLink,
                  }}
                >
                  <Typography>
                    This page is designed to help you dream about your next
                    home. Select the floor plan, size and DesignSmart® features
                    that fit your lifestyle, then browse homes near you that
                    match your preferences.
                  </Typography>
                </IconCard>
              </Hero>
              <section className="recommendations-section">
                <div className="container">
                  <ItemsGrid
                    heading="Recommendations to explore on this page:"
                    items={[
                      {
                        children: (
                          <span
                            onClick={() => smoothScroll('#plannerSection', -20)}
                          >
                            <img src={bars} alt="bars icon" />
                            Tell Us About Your Home Preferences
                          </span>
                        ),
                      },
                      {
                        children: (
                          <span
                            onClick={() => smoothScroll('#plannerSection', -20)}
                          >
                            <img src={heart} alt="heart icon" />
                            Favorite Some Features
                          </span>
                        ),
                      },
                      {
                        children: (
                          <span
                            onClick={() =>
                              smoothScroll('#builtWithYouInMind', -20)
                            }
                          >
                            <img src={grid} alt="grid icon" />
                            HouseSmart. Homes with you in mind.
                          </span>
                        ),
                      },
                    ]}
                    components={{
                      Item: Button,
                    }}
                    componentsProps={{
                      Heading: {
                        variant: 'p',
                      },
                      Item: {
                        variant: 'outline',
                        color: '#4E5255',
                        size: 'small',
                      },
                    }}
                  />
                </div>
              </section>
              <section className="planner-section" id="plannerSection">
                <div className="container">
                  <div className="planner-box">
                    {updatedUserProfile !== null ? (
                      <>
                        {!formIsSaved ? (
                          <PlannerQuestionnaire
                            activeQuestion={activeQuestion}
                            onSave={() => {
                              setFormIsSaved(true) +
                                setAspirationFormSavedHCs((hc) => [
                                  ...hc,
                                  dealer?.dealerId,
                                ]) +
                                setAspirationFormSavedProfileIds((p) => [
                                  ...p,
                                  updatedUserProfile?.profileId,
                                ]);
                            }}
                            handlers={{
                              onClick: (e) => {
                                smoothScroll('.planner-box', -20) +
                                  setActiveQuestion(activeQuestion + 1);
                              },
                              onPrevClick: (e) => {
                                smoothScroll('.planner-box', -20) +
                                  setActiveQuestion(activeQuestion - 1);
                              },
                            }}
                            componentsProps={{
                              Trigger: {
                                disabled: bedBathButtonDisabled,
                              },
                            }}
                            components={{
                              Trigger:
                                activeQuestion === 1 &&
                                ((updatedUserProfile?.preferredBeds &&
                                  !updatedUserProfile?.preferredBaths) ||
                                  (updatedUserProfile?.preferredBaths &&
                                    !updatedUserProfile?.preferredBeds))
                                  ? TriggerBtn
                                  : null,
                            }}
                            children={[
                              <PlannerQuestion
                                type="custom"
                                className="select-box-question"
                                heading="Figuring out what you want in your dream home is an important step in the home buying aspiration. Answering the next few questions helps us recommend homes to you based off your preferences."
                                note="How many bedrooms and bathrooms do you need?"
                                children={[
                                  <div className="selectbox-wrap">
                                    <TextInput
                                      name="bedroomselectbox"
                                      label="Bedrooms"
                                      color="#4E5255"
                                      variant="outline"
                                      fullwidth
                                      id="bedroomSelect"
                                      vanity
                                      value={
                                        updatedUserProfile?.preferredBeds ||
                                        null
                                      }
                                      note={bedError || null}
                                      values={[
                                        {
                                          label: '1',
                                          value: 1,
                                        },
                                        {
                                          label: '2',
                                          value: 2,
                                        },
                                        {
                                          label: '3',
                                          value: 3,
                                        },
                                        {
                                          label: '4',
                                          value: 4,
                                        },
                                      ]}
                                      handlers={{
                                        onChange: (value) =>
                                          handleProfileFieldUpdate(
                                            'preferredBeds'
                                          )(parseFloat(value)),
                                      }}
                                    />
                                    <TextInput
                                      name="bathroomselectbox"
                                      label="Bathrooms"
                                      color="#4E5255"
                                      variant="outline"
                                      fullwidth
                                      id="bathroomSelect"
                                      vanity
                                      value={
                                        updatedUserProfile?.preferredBaths ||
                                        null
                                      }
                                      note={bathError || null}
                                      values={[
                                        {
                                          label: '1',
                                          value: 1,
                                        },
                                        {
                                          label: '2',
                                          value: 2,
                                        },
                                        {
                                          label: '3',
                                          value: 3,
                                        },
                                      ]}
                                      handlers={{
                                        onChange: (value) =>
                                          handleProfileFieldUpdate(
                                            'preferredBaths'
                                          )(parseFloat(value)),
                                      }}
                                    />
                                  </div>,
                                ]}
                              ></PlannerQuestion>,
                              <PlannerQuestion
                                type="custom"
                                heading="Thinking about the ways a home reflects your priorities in life, what are the features you want for your floor plan?"
                                note="Choose the features that fit your lifestyle. Not all features are available on all models."
                              >
                                {featureCardList && featureCardList.length > 0 && (
                                  <>
                                    <FeaturesGrid>
                                      {dxContext?.state?.userServiceLoading && (
                                        <div className="features-loading-screen">
                                          <Loading width="50px" />
                                        </div>
                                      )}
                                      {featureCardList.map((feature) => (
                                        <FeatureCard
                                          icon={
                                            <RemoteSVG src={feature?.url} />
                                          }
                                          label={feature.name}
                                          featureId={feature.featureId}
                                          key={feature.featureId}
                                          favorite={feature?.favorite}
                                          disabled={
                                            feature?.favorite
                                              ? false
                                              : isDisabled
                                          }
                                          handlers={{
                                            onClick: () =>
                                              handleFeatureClick(
                                                feature.featureId,
                                                feature?.name
                                              ),
                                          }}
                                        />
                                      ))}
                                    </FeaturesGrid>
                                    {isMobile &&
                                      dxContext?.state?.account?.profile
                                        ?.likedFeatures &&
                                      dxContext?.state?.account?.profile
                                        ?.likedFeatures.length > 0 && (
                                        <div className="featured-selections-mobile">
                                          <div className="featured-selections-mobile-grid">
                                            <p className="headline">
                                              Your Selections:
                                            </p>
                                            {dxContext?.state?.account?.profile?.likedFeatures.map(
                                              (feature) => (
                                                <FeatureCard
                                                  icon={
                                                    <RemoteSVG
                                                      src={feature?.url}
                                                    />
                                                  }
                                                  label={feature.name}
                                                  featureId={feature.featureId}
                                                  key={feature.featureId}
                                                  favorite={true}
                                                  disabled={true}
                                                  handlers={{
                                                    onClick: () =>
                                                      handleFeatureClick(
                                                        feature.featureId,
                                                        feature?.name
                                                      ),
                                                  }}
                                                />
                                              )
                                            )}
                                          </div>
                                        </div>
                                      )}
                                  </>
                                )}
                              </PlannerQuestion>,
                            ]}
                          />
                        ) : (
                          <PlannerFields
                            heading="Home Preferences"
                            subheading="Your Answers"
                          >
                            <Stack
                              direction={{
                                large: 'row',
                              }}
                              justifyContent="space-evenly"
                              fullwidth
                            >
                              <PlannerField
                                heading="Beds"
                                answer={
                                  updatedUserProfile?.preferredBeds
                                    ? updatedUserProfile?.preferredBeds == '1'
                                      ? updatedUserProfile?.preferredBeds +
                                        ' bedroom'
                                      : updatedUserProfile?.preferredBeds +
                                        ' bedrooms'
                                    : 'No preference'
                                }
                                components={{
                                  Action: () =>
                                    CustomAction(
                                      updatedUserProfile?.preferredBeds,
                                      1
                                    ),
                                }}
                              />
                              <PlannerField
                                heading="Baths"
                                answer={
                                  updatedUserProfile?.preferredBaths
                                    ? updatedUserProfile?.preferredBaths == '1'
                                      ? updatedUserProfile?.preferredBaths +
                                        ' bathroom'
                                      : updatedUserProfile?.preferredBaths +
                                        ' bathrooms'
                                    : 'No preference'
                                }
                                components={{
                                  Action: () =>
                                    CustomAction(
                                      updatedUserProfile?.preferredBaths,
                                      1
                                    ),
                                }}
                              />
                            </Stack>
                            {dxContext?.state?.account?.profile
                              ?.likedFeatures ? (
                              <div className="feature-answer-wrap">
                                <h2>Favorite Features</h2>
                                <FeaturesGrid>
                                  {dxContext?.state?.account?.profile?.likedFeatures.map(
                                    (feature) => (
                                      <FeatureCard
                                        icon={<RemoteSVG src={feature?.url} />}
                                        label={feature.name}
                                        featureId={feature.featureId}
                                        key={feature.featureId}
                                        favorite={true}
                                      />
                                    )
                                  )}
                                  <Button
                                    variant="outline"
                                    className="edit-features-btn"
                                    handlers={{
                                      onClick: () => {
                                        setFormIsSaved(false) +
                                          setActiveQuestion(2);
                                      },
                                    }}
                                  >
                                    <Pencil />
                                    Edit Favorite Features
                                  </Button>
                                </FeaturesGrid>
                              </div>
                            ) : (
                              <PlannerField
                                heading="Favorite Features"
                                answer={
                                  'You haven’t selected your favorite features'
                                }
                                components={{
                                  Action: () =>
                                    CustomAction(
                                      updatedUserProfile?.preferredBaths,
                                      1
                                    ),
                                }}
                              />
                            )}
                          </PlannerFields>
                        )}
                      </>
                    ) : (
                      <div className="planner-loading-screen">
                        <Loading width="100px" />
                      </div>
                    )}
                  </div>
                </div>
              </section>
              <InView
                triggerOnce
                as="section"
                className="left-heading-section"
                id="builtWithYouInMind"
                threshold={0.3}
                onChange={(inView, entry) =>
                  inView
                    ? dxContext?.actions?.markContentComplete(
                        aspirationsPlannerProgressObj,
                        'home_aspirations_info'
                      )
                    : null
                }
              >
                <div className="container">
                  <div className="left-heading-component">
                    <div className="left-side">
                      <p className="left-heading">
                        HouseSmart. Homes with you in mind.
                      </p>
                    </div>
                    <div className="right-side">
                      <div className="quad-columns">
                        <div className="quad-column">
                          <div className="quad-logo-wrap">
                            <img
                              className="quad-logo quad-logo-builtsmart"
                              src={BuiltSmartLogo}
                              alt="BuiltSmart Logo"
                            />
                          </div>
                          <p>
                            We use quality products from the world’s most
                            trusted brands, so your home is built to last*.
                            We’re also building a better tomorrow through
                            environmental management, renewable energy and waste
                            reduction, which can lower costs for you.
                          </p>
                          <p className="small-p">
                            *With proper home maintenance.
                          </p>
                        </div>
                        <div className="quad-column">
                          <div className="quad-logo-wrap">
                            <img
                              className="quad-logo quad-logo-designsmart"
                              src={DesignSmartLogo}
                              alt="DesignSmart Logo"
                            />
                          </div>
                          <p>
                            Smart design features are the difference between a
                            house and a dream home. It’s open spaces that
                            inspire togetherness, storage solutions to make life
                            easier, stylish exterior options and spa bathrooms,
                            all designed with you in mind.
                          </p>
                        </div>
                        <div className="quad-column">
                          <div className="quad-logo-wrap">
                            <img
                              className="quad-logo quad-logo-energysmart"
                              src={EnergySmartLogo}
                              alt="EnergySmart Logo"
                            />
                          </div>
                          <p>
                            Enjoy a whole new level of home performance in
                            energysavings, comfort, health and durability. Made
                            with energy efficient features that come together to
                            save you tens of thousands* over the life of your
                            home.
                          </p>
                          <p className="small-p">
                            <a
                              href="https://stage.energy.gov/eere/buildings/articles/ultra-efficient-fact-sheet"
                              target="_blank"
                            >
                              *Source
                            </a>
                            <span>
                              † EnergySmart Zero™ homes are built with energy
                              efficient features and made to DOE Energy Ready
                              Home™ program specifications.
                            </span>
                          </p>
                        </div>
                        <div className="quad-column">
                          <div className="quad-logo-wrap">
                            <img
                              className="quad-logo quad-logo-budgetsmart"
                              src={BudgetSmartLogo}
                              alt="BudgetSmart Logo"
                            />
                          </div>
                          <p>
                            We’re working to make the dream of homeownership
                            possible for everyone. Our precise building process
                            and energy efficient features can save you money so
                            you can open doors to a better life.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </InView>
              {homesList && homesList.length > 0 && (
                <RecommendedHomes
                  subHeading={
                    aspirationHomes && aspirationHomes.length > 0
                      ? `Based on your home features preferences`
                      : `Featured Homes at ${formatDealerName(
                          dealer?.dealerName
                        )}`
                  }
                  listItems={homesList}
                  homes={homes}
                  dealer={dealer}
                  retailEventLabel="portal_my_home_aspirations_recommended_homes"
                />
              )}
              <section className="explore-section">
                <div className="container">
                  <ItemsGrid
                    heading="Next Step"
                    items={[
                      {
                        children: {
                          icon: (
                            <img
                              src={ReviewNextStepIcon}
                              alt="review next step icon"
                            />
                          ),
                          chip: {
                            text: 'Planner',
                            background: 'rgba(141, 131, 190, 0.2);',
                            color: 'rgba(37, 41, 45, 0.5)',
                          },
                          heading: 'Review Your Plan',
                        },
                      },
                    ]}
                    components={{
                      Item: PreferencesLink,
                    }}
                  />
                </div>
              </section>
            </>
          )}
        </MyHomeAspirationsStyles>
      </App>
    </ThemeProvider>
  );
};

MyHomeAspirations.getInitialProps = async (ctx) => {
  return await parseInitialPropsContext(ctx, dealerService, homesService).catch(
    (e) => {
      console.error(e);
      return { redirectTo: '/error' };
    }
  );
};

export default MyHomeAspirations;
